import { Stack, Typography } from '@mui/material';
import { MilestoneDto } from 'tdc-web-backend/milestones/schemas';
import { ProjectDto } from 'tdc-web-backend/projects/schemas';
import { EnumCurrency, PaymentStatus } from 'tdc-web-backend/enums/enums';
import { useTranslation } from 'react-i18next';
import { PaymentsList } from '../../Project/payments/PaymentsList';
import RecentPayments from './RecentPayments';
import useGetMany from '../../../../utils/hooks/crud-hooks/useGetMany';
import PaymentLinearProgressCard from './PaymentLinearProgressCard';
import RevenueChart from '../../ProjectDetail/RevenueChart/RevenueChart';
import DashboardCard from '../../../../components/dashboard-card/EndToEndCard.tsx/DashboardCard';
import useAuth from '../../../../utils/hooks/useAuth';
import { useEffect, useState } from 'react';
import {
  primaryLight,
  secondaryGreen,
  secondaryOrange,
  secondaryPink,
} from '../../../../utils/color';
import useMode from '../../../../utils/hooks/useMode';
import { EnumMode } from '../../../../utils/context/Mode.context';

const AllPaymentsScreen = () => {
  const { t } = useTranslation();
  const {
    authData: { userData },
  } = useAuth();

  const { mode } = useMode();

  const [paidEur, setPaidEur] = useState(0);
  const [paidUsd, setPaidUsd] = useState(0);
  const [unpaidEur, setUnpaidEur] = useState(0);
  const [unpaidUsd, setUnpaidUsd] = useState(0);
  const [percentageEur, setPercentageEur] = useState(0);
  const [percentageUsd, setPercentageUsd] = useState(0);

  const { data: paymentsData } = useGetMany<MilestoneDto>({
    resource: `/milestones?${mode === EnumMode.Supplier ? 'seller' : 'buyer'}=${
      userData?.membership?.company
    }`,
  });
  const { data: projects } = useGetMany<ProjectDto>({
    resource: `/projects?${mode === EnumMode.Supplier ? 'seller' : 'buyer'}=${
      userData?.membership?.company
    }`,
  });

  useEffect(() => {
    let totalPaidEur = 0;
    let totalPaidUsd = 0;
    let totalUnpaidEur = 0;
    let totalUnpaidUsd = 0;

    paymentsData?.data.results.forEach((payment) => {
      if (payment.paymentStatus === PaymentStatus.Paid) {
        if (payment.currency === EnumCurrency.Eur) {
          totalPaidEur += payment.budget;
        } else if (payment.currency === EnumCurrency.Usd) {
          totalPaidUsd += payment.budget;
        }
      } else if (payment.currency === EnumCurrency.Eur) {
        totalUnpaidEur += payment.budget;
      } else if (payment.currency === EnumCurrency.Usd) {
        totalUnpaidUsd += payment.budget;
      }
    });

    setPaidEur(totalPaidEur);
    setPaidUsd(totalPaidUsd);
    setUnpaidEur(totalUnpaidEur);
    setUnpaidUsd(totalUnpaidUsd);

    const totalEur = totalPaidEur + totalUnpaidEur;
    const totalUsd = totalPaidUsd + totalUnpaidUsd;

    setPercentageEur(totalEur ? (totalPaidEur / totalEur) * 100 : 0);
    setPercentageUsd(totalUsd ? (totalPaidUsd / totalUsd) * 100 : 0);
  }, [paymentsData]);

  return (
    <>
      <Stack spacing={5} sx={{ py: 2 }}>
        <Stack spacing={0.2}>
          <Typography variant="h4">{t('common.payments')}</Typography>
        </Stack>
        <Stack spacing={4} direction="row">
          <DashboardCard
            mainCardClassName="react-joyride-step-1-payments"
            headline={t('common.totalBalance')}
            sx={{ width: '49%' }}
            renderComponent={
              <RevenueChart milestones={paymentsData?.data.results as MilestoneDto[]} />
            }
            renderComponentSx={{ height: '100%' }}
          />

          <DashboardCard
            mainCardClassName="react-joyride-step-2-payments"
            headline={t('common.paymentSummary')}
            sx={{ width: '49%' }}
            renderComponent={<RecentPayments rows={paymentsData?.data.results ?? []} />}
          />
        </Stack>
        <Stack spacing={1}>
          {paidEur + unpaidEur > 0 && (
            <PaymentLinearProgressCard
              paid={paidEur}
              unpaid={unpaidEur}
              percentage={percentageEur}
              currency={EnumCurrency.Eur}
              backgroundColor={primaryLight[300]}
              progressColor={primaryLight[500]}
            />
          )}
          {paidUsd + unpaidUsd > 0 && (
            <PaymentLinearProgressCard
              paid={paidUsd}
              unpaid={unpaidUsd}
              percentage={percentageUsd}
              currency={EnumCurrency.Usd}
              backgroundColor={secondaryGreen[601]}
              progressColor={secondaryGreen[801]}
            />
          )}
        </Stack>
        <PaymentsList payments={projects?.data.results || []} />
      </Stack>
    </>
  );
};

export default AllPaymentsScreen;
